import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment/moment'
import i18n from 'simple-react-i18n'
import { groupBy, range } from 'lodash'
import { Grid } from '@mui/material'
import PluviometryAction from '../../../actions/PluviometryAction'
import PluviometerDto from '../../../dto/PluviometerDto'
import DtoPluvioMeasure from '../../../dto/measures/DtoPluvioMeasure'
import ProgressCard from '../../../../components/card/ProgressCard'
import EChartV6Pluvio from '../../../../components/echart/EChartV6Pluvio'
import { exportPictureIcon } from '../../../../components/echart/EChartUtils'
import { getMonthList, getYear } from '../../../../utils/DateUtil'
import { DATATYPE_RAIN } from '../../../../utils/PluviometryUtils'
import { SUM_MONTH } from '../../../constants/PluvioOptionConstant'

const MONTH_COLORS = [
    '#148F77', '#1B4F72', '#21618C', '#85C1E9',
    '#17A1B4', '#82BFC8', '#456469', '#4B4569',
    '#1C0F5C', '#0F5C4C', '#100F5C', '#8685C4',
]
const MONTHS_PER_YEAR = 12
const CHART_HEIGHT = 550

const buildYearRange = (firstDate) => range(getYear(firstDate), getYear(moment().valueOf()) + 1)

const buildEmptyYearMonths = (year) => range(0, MONTHS_PER_YEAR).map(month => [
    moment().year(year).month(month).valueOf(),
    0,
])

const buildSeries = (data, years) => {
    const zeroFill = years.flatMap(buildEmptyYearMonths)
    const dataByMonth = groupBy([...data, ...zeroFill], m => moment(m[0]).month())
    const monthNames = getMonthList().map(m => m.name)
    return MONTH_COLORS.map((color, idx) => ({
        name: monthNames[idx],
        type: 'bar',
        stack: 'monthsStack',
        label: { show: false },
        itemStyle: { color },
        data: (dataByMonth[idx] ?? []).map(v => v[1]),
    }))
}

const Graph = ({ pluviometerInfos, data }) => {
    const years = buildYearRange(data[0]?.[0])

    const option = {
        title: {
            text: `[${pluviometerInfos.code}] - ${pluviometerInfos.name} - ${i18n.monthsStacked}`,
            left: 'center',
            top: 10,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
        },
        toolbox: {
            show: true,
            right: 65,
            feature: {
                saveAsImage: { title: i18n.export, icon: exportPictureIcon },
            },
        },
        legend: {
            data: getMonthList().map(m => m.name),
            bottom: 0,
            type: 'scroll',
        },
        grid: {
            top: 60,
            height: 450,
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true,
        },
        xAxis: [{
            type: 'category',
            data: years,
            splitLine: { show: true },
        }],
        yAxis: [{
            type: 'value',
            name: `${i18n.rain} [${i18n.milimetersSymbol}]`,
            nameLocation: 'middle',
            nameGap: 40,
            splitLine: { show: true },
        }],
        series: buildSeries(data, years),
    }

    return (
        <Grid sx={{ paddingTop: '30px' }}>
            <EChartV6Pluvio option={option} style={{ height: CHART_HEIGHT }} />
        </Grid>
    )
}

Graph.propTypes = {
    pluviometerInfos: PropTypes.shape({
        code: PropTypes.string,
        name: PropTypes.string,
    }),
    data: PropTypes.arrayOf(PropTypes.instanceOf(DtoPluvioMeasure)),
}

const GraphMonthsStackRain = ({
    pluviometer,
}) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)

    const dispatch = useDispatch()
    const pluvioMeasures = useSelector(state => state.PluviometryReducer.pluvioMeasures)

    useEffect(() => {
        setDataLoaded(false)
        const rainInput = {
            stationId: pluviometer.id,
            dataType: DATATYPE_RAIN,
            groupFunc: SUM_MONTH,
            chartMode: true,
        }
        dispatch(PluviometryAction.loadPluvioChronicMeasures([rainInput], setProgress))
            .then(() => setDataLoaded(true))
    }, [dispatch, pluviometer])

    if (!dataLoaded) {
        return (
            <ProgressCard progress={progress} />
        )
    }

    const data = pluvioMeasures[0]?.measures.map(m => [m.date, m.value]) ?? []

    return (
        <Graph
            pluviometerInfos={{ code: pluviometer.code, name: pluviometer.name }}
            data={data}
        />
    )
}

GraphMonthsStackRain.propTypes = {
    pluviometer: PropTypes.instanceOf(PluviometerDto),
}

export default GraphMonthsStackRain
